import { makeAutoObservable } from "mobx";
import type RootStore from "stores/RootStore";
import type ServiceStore from "stores/ServiceStore";
// Using local types that match the UI's expected structure
import type { Invoice, InvoiceLineItem } from "../../types";
import { InvoiceStatus } from "../../types";
// import { databaseDate } from '@intrasync/utils';

class InvoicesStore {
  rootStore: RootStore;

  // Observables - Only UI state
  selectedInvoiceId: string | null = null;
  invoiceFormData: Partial<Invoice> = {};
  lineItemFormData: Partial<InvoiceLineItem> = {};
  isFormOpen = false;
  isLineItemFormOpen = false;
  formMode: "create" | "edit" = "create";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      rootStore: false,
    });
  }

  // Get service stores
  get invoicesStore(): ServiceStore {
    return this.rootStore.invoicesStore as ServiceStore;
  }

  // Computed values
  get selectedInvoice(): Invoice | null {
    return this.selectedInvoiceId
      ? this.invoicesStore.entities.find((i: any) => i.id === this.selectedInvoiceId) as unknown as Invoice
      : null;
  }

  get invoices(): Invoice[] {
    const invoices = this.invoicesStore.entities as unknown as Invoice[];
    return invoices || [];
  }

  // Form management
  openForm = (mode: "create" | "edit" = "create", invoiceId?: string) => {
    this.formMode = mode;
    this.isFormOpen = true;
    if (mode === "edit" && invoiceId) {
      const invoice = this.invoicesStore.entities.find((i: any) => i.id === invoiceId) as unknown as Invoice;
      if (invoice) {
        this.setInvoiceFormData(invoice);
        this.selectedInvoiceId = invoiceId;
      }
    } else {
      this.resetInvoiceFormData();
    }
  };

  closeForm = () => {
    this.isFormOpen = false;
    this.resetInvoiceFormData();
    this.selectedInvoiceId = null;
  };

  openLineItemForm = (lineItem?: InvoiceLineItem) => {
    this.isLineItemFormOpen = true;
    if (lineItem) {
      this.setLineItemFormData(lineItem);
    } else {
      this.resetLineItemFormData();
    }
  };

  closeLineItemForm = () => {
    this.isLineItemFormOpen = false;
    this.resetLineItemFormData();
  };

  // Actions
  setSelectedInvoice = (invoiceId: string | null) => {
    this.selectedInvoiceId = invoiceId;
  };

  setInvoiceFormData = (data: Partial<Invoice>) => {
    this.invoiceFormData = { ...this.invoiceFormData, ...data };
  };

  setLineItemFormData = (data: Partial<InvoiceLineItem>) => {
    this.lineItemFormData = { ...this.lineItemFormData, ...data };
  };

  resetInvoiceFormData = () => {
    this.invoiceFormData = {
      status: InvoiceStatus.DRAFT,
      lineItems: [],
      dueDate: "",
      subtotal: 0,
      taxAmount: 0,
      totalAmount: 0,
    };
  };

  resetLineItemFormData = () => {
    this.lineItemFormData = {
      quantity: 1,
      unitPrice: 0,
      total: 0,
    };
  };

  // Async actions
  createInvoice = async (
    invoiceData: Partial<Invoice>
  ): Promise<Invoice | null> => {
    try {
      const result = await this.invoicesStore.create(invoiceData);
      return result as unknown as Invoice;
    } catch (error) {
      console.error("Failed to create invoice:", error);
      throw error;
    }
  };

  updateInvoice = async (
    id: string,
    invoiceData: Partial<Invoice>
  ): Promise<Invoice | null> => {
    try {
      const result = await this.invoicesStore.patch(parseInt(id), invoiceData);
      return result as unknown as Invoice;
    } catch (error) {
      console.error("Failed to update invoice:", error);
      throw error;
    }
  };

  deleteInvoice = async (id: string): Promise<boolean> => {
    try {
      await this.invoicesStore.remove(parseInt(id));
      return true;
    } catch (error) {
      console.error("Failed to delete invoice:", error);
      throw error;
    }
  };

  // Line item management
  addLineItem = (lineItem: InvoiceLineItem) => {
    if ((this.invoiceFormData as any).lineItems) {
      (this.invoiceFormData as any).lineItems = [
        ...(this.invoiceFormData as any).lineItems,
        lineItem,
      ];
    } else {
      (this.invoiceFormData as any).lineItems = [lineItem];
    }
    this.calculateTotals();
  };

  updateLineItem = (index: number, lineItem: Partial<InvoiceLineItem>) => {
    if (
      this.invoiceFormData.lineItems &&
      this.invoiceFormData.lineItems[index]
    ) {
      this.invoiceFormData.lineItems[index] = {
        ...this.invoiceFormData.lineItems[index],
        ...lineItem,
      };
      this.calculateTotals();
    }
  };

  removeLineItem = (index: number) => {
    if (this.invoiceFormData.lineItems) {
      this.invoiceFormData.lineItems = this.invoiceFormData.lineItems.filter(
        (_, i) => i !== index
      );
      this.calculateTotals();
    }
  };

  calculateTotals = () => {
    if (!this.invoiceFormData.lineItems) return;

    const subtotal = this.invoiceFormData.lineItems.reduce(
      (sum, item) => sum + item.total,
      0
    );

    this.invoiceFormData.subtotal = subtotal;
    this.invoiceFormData.totalAmount =
      subtotal + (this.invoiceFormData.taxAmount || 0);
  };

  // Status management
  markAsSent = async (id: string): Promise<boolean> => {
    return this.updateInvoiceStatus(id, InvoiceStatus.SENT);
  };

  markAsPaid = async (id: string): Promise<boolean> => {
    return this.updateInvoiceStatus(id, InvoiceStatus.PAID);
  };

  markAsOverdue = async (id: string): Promise<boolean> => {
    return this.updateInvoiceStatus(id, InvoiceStatus.OVERDUE);
  };

  private updateInvoiceStatus = async (
    id: string,
    status: Invoice["status"]
  ): Promise<boolean> => {
    try {
      await this.invoicesStore.patch(parseInt(id), { status });
      return true;
    } catch (error) {
      console.error("Failed to update invoice status:", error);
      throw error;
    }
  };

  // Computed properties
  get overdueInvoices() {
    const today = new Date().toISOString().split("T")[0];
    return this.invoices.filter(
      (invoice) =>
        invoice.status !== InvoiceStatus.PAID && invoice.dueDate < today
    );
  }

  get totalReceivables() {
    return this.invoices
      .filter((invoice) => invoice.status !== InvoiceStatus.PAID)
      .reduce((sum, invoice) => sum + invoice.totalAmount, 0);
  }

  get overdueReceivables() {
    return this.overdueInvoices.reduce(
      (sum, invoice) => sum + invoice.totalAmount,
      0
    );
  }

  loadInvoices = async () => {
    try {
      await this.invoicesStore.find({});
    } catch (error) {
      console.error("Failed to load invoices:", error);
      throw error;
    }
  };

  get isLoading(): boolean {
    return this.invoicesStore.isServicePending;
  }
}

export default InvoicesStore;
